'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useAuth } from '@/contexts/auth-context';
import { takeSigninPrefillEmail } from '@/lib/auth/signin-prefill';
import { phCapture } from '@/utils/analytics';
import { getBrowserClient } from '@/utils/supabase/client';
import { resolvePostLoginRedirect } from '@/lib/auth/post-login-redirect';
import { categorizeOtpError, otpCooldownSeconds, otpErrorCopy } from '@/lib/auth/otp-errors';
import { SCRAPER_NOTE } from '@/lib/odds-screen/automation';

const IS_DEV = process.env.NODE_ENV === 'development';
import { Loader2, Lock, Info, CheckCircle2 } from 'lucide-react';
import { OTPInput } from '@/components/auth/otp-input';
import { GoogleSignInButton } from '@/components/auth/google-sign-in-button';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { StatePanel } from '@/components/ui/state-panel';
import { MobileReviewerSignin } from '@/components/auth/mobile-reviewer-signin';
import { openSupportChat } from '@/components/ui/intercom/open-support-chat';

type SignInState =
  | 'email_entry'
  | 'otp_sending'
  | 'otp_sent'
  | 'otp_verifying'
  | 'success';

export function SignInForm({
  lastTool,
  googleAvailable,
}: {
  lastTool: string | null;
  googleAvailable: boolean;
}) {
  useEffect(() => {
    phCapture('signin_viewed');
  }, []);

  const [state, setState] = useState<SignInState>('email_entry');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resendTimer, setResendTimer] = useState(0);
  const [codeAlreadyRequested, setCodeAlreadyRequested] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const {
    sendOTPCode,
    verifyOTPAndSignIn,
    resendOTPCode,
    isAuthenticated,
    isLoading: authLoading
  } = useAuth();

  const searchParams = useSearchParams();
  const redirectTo = searchParams?.get('redirect_to') || searchParams?.get('redirect');
  const message = searchParams?.get('message');
  const prefillEmail = searchParams?.get('email')?.trim() || '';
  const showGoogle = googleAvailable && message !== 'google-use-email';

  useEffect(() => {
    const stored = takeSigninPrefillEmail();
    if (stored) {
      setEmail(stored);
      return;
    }
    if (prefillEmail) {
      setEmail(prefillEmail);
    }
  }, [prefillEmail]);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      setIsRedirecting(true);
      window.location.href = resolvePostLoginRedirect(redirectTo, lastTool);
    }
  }, [isAuthenticated, authLoading, redirectTo, lastTool]);

  const formatResend = (s: number) => {
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleOpenHelp = () => {
    openSupportChat();
  };

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setState('otp_sending');

    const { error } = await sendOTPCode(email);

    if (error) {
      const cooldown = otpCooldownSeconds(error.message);
      if (cooldown !== null) {
        setState('otp_sent');
        setCodeAlreadyRequested(true);
        setResendTimer(cooldown);
        return;
      }
      setState('email_entry');
      setError(otpErrorCopy(categorizeOtpError(error.message), 'send', error.message));
      return;
    }

    setState('otp_sent');
    setCodeAlreadyRequested(false);
    setResendTimer(60);
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (otp.length !== 6) {
      setError('Please enter the complete 6-digit code.');
      return;
    }

    setError(null);
    setState('otp_verifying');

    const { error } = await verifyOTPAndSignIn(email, otp);

    if (error) {
      setState('otp_sent');
      setError(otpErrorCopy(categorizeOtpError(error.message), 'verify', error.message));
      setOtp('');
      return;
    }

    setState('success');
    setIsRedirecting(true);
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;

    setError(null);
    const { error } = await resendOTPCode(email);

    if (error) {
      const cooldown = otpCooldownSeconds(error.message);
      if (cooldown !== null) {
        setCodeAlreadyRequested(true);
        setResendTimer(cooldown);
        return;
      }
      setError(otpErrorCopy(categorizeOtpError(error.message), 'resend', error.message));
      return;
    }

    setCodeAlreadyRequested(false);
    setResendTimer(60);
    setOtp('');
  };

  const handleEditEmail = () => {
    setState('email_entry');
    setCodeAlreadyRequested(false);
    setOtp('');
    setError(null);
  };

  const [devPassword, setDevPassword] = useState('');
  const [devError, setDevError] = useState<string | null>(null);
  const [devLoading, setDevLoading] = useState(false);

  const handleDevLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setDevError(null);
    setDevLoading(true);
    try {
      const supabase = getBrowserClient();
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password: devPassword,
      });
      if (error) {
        setDevError(error.message);
        setDevLoading(false);
        return;
      }
      setIsRedirecting(true);
      window.location.href = resolvePostLoginRedirect(redirectTo, lastTool);
    } catch (err) {
      setDevError(err instanceof Error ? err.message : 'Login failed');
      setDevLoading(false);
    }
  };

  const signUpParams = new URLSearchParams();
  if (redirectTo) signUpParams.set('redirect', redirectTo);
  if (email) signUpParams.set('email', email);
  const signUpHref = signUpParams.toString() ? `/signup?${signUpParams}` : '/signup';

  if (authLoading || isRedirecting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="flex flex-col items-center gap-4 text-center">
          <Spinner size="lg" />
          <p className="text-foreground">
            {isRedirecting ? 'Redirecting…' : 'Authenticating…'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      <p role="status" className="sr-only" data-ph-mask>
        {codeAlreadyRequested &&
          `A code was already requested for ${email} in the last minute. Enter it if you have it, or get a new one when the timer runs out.`}
      </p>
      <div className="mb-6 flex justify-center">
        <Image
          src="/images/sportsbooks/upside_logo.png"
          alt="Upside Logo"
          width={96}
          height={32}
          sizes="96px"
          className="h-8 w-auto"
        />
      </div>

      {message === 'existing-account' && (
        <div className="mb-4 w-full max-w-md flex items-start gap-3 rounded-panel border border-white/[0.08] bg-surface-raised px-4 py-3">
          <Info className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm text-foreground">
            We found an existing account for this email. Please sign in to continue.
          </p>
        </div>
      )}
      {message === 'account-upgraded' && (
        <div className="mb-4 w-full max-w-md flex items-start gap-3 rounded-panel border border-accent-value/30 bg-accent-value/5 px-4 py-3">
          <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-accent-value" aria-hidden="true" />
          <p className="text-sm text-foreground">
            Your account has been set up! You can now sign in.
          </p>
        </div>
      )}
      {message === 'billing' && (
        <div className="mb-4 w-full max-w-md flex items-start gap-3 rounded-panel border border-white/[0.08] bg-surface-raised px-4 py-3">
          <Info className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm text-foreground">
            Sign in to fix your payment and get your access back.
          </p>
        </div>
      )}
      {message === 'banned' && (
        <div className="mb-4 w-full max-w-md flex items-start gap-3 rounded-panel border border-white/[0.08] bg-surface-raised px-4 py-3">
          <Info className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm text-foreground">{SCRAPER_NOTE}</p>
        </div>
      )}
      {message === 'google-failed' && (
        <div className="mb-4 w-full max-w-md flex items-start gap-3 rounded-panel border border-white/[0.08] bg-surface-raised px-4 py-3">
          <Info className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm text-foreground">
            Google sign-in didn't finish. Try again, or use your email instead.
          </p>
        </div>
      )}
      {message === 'google-use-email' && (
        <div className="mb-4 w-full max-w-md flex items-start gap-3 rounded-panel border border-white/[0.08] bg-surface-raised px-4 py-3">
          <Info className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm text-foreground">
            This Gmail already has an Upside account under a different spelling. Sign in with an email code, using the address you signed up with.
          </p>
        </div>
      )}
      {message === 'no-password-needed' && (
        <div className="mb-4 w-full max-w-md flex items-start gap-3 rounded-panel border border-white/[0.08] bg-surface-raised px-4 py-3">
          <Info className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm text-foreground">
            Passwords are no longer needed! We'll send you a verification code to sign in.
          </p>
        </div>
      )}

      {IS_DEV && (state === 'email_entry' || state === 'otp_sending') && (
        <form onSubmit={handleDevLogin} className="mb-4 p-4 max-w-md w-full bg-warning/5 border border-warning/30 rounded-panel">
          <p className="text-xs data-num text-warning mb-3">DEV: Password Login</p>
          <div className="flex gap-2">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="flex-1"
            />
            <Input
              type="password"
              value={devPassword}
              onChange={(e) => setDevPassword(e.target.value)}
              placeholder="Password"
              className="flex-1"
            />
            <Button type="submit" size="sm" disabled={devLoading}>
              {devLoading ? '…' : 'Go'}
            </Button>
          </div>
          {devError && <p className="text-destructive text-xs mt-2">{devError}</p>}
        </form>
      )}

      {(state === 'email_entry' || state === 'otp_sending') && (
        <form onSubmit={handleSendOTP} className="p-6 max-w-md w-full rounded-panel border border-white/[0.08] bg-surface-raised surface-toplight">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-semibold tracking-[-0.02em] text-foreground">Welcome back</h1>
            <p className="mt-2 text-sm text-muted-foreground">Log in to your Upside account.</p>
          </div>
          {showGoogle && (
            <>
              <GoogleSignInButton redirectTo={redirectTo} />
              <div className="my-5 flex items-center gap-3">
                <span className="h-px flex-1 bg-white/[0.08]" />
                <span className="text-xs text-muted-foreground">or</span>
                <span className="h-px flex-1 bg-white/[0.08]" />
              </div>
            </>
          )}
          <div className="mb-4">
            <Label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              autoFocus={!showGoogle}
              disabled={state === 'otp_sending'}
            />
          </div>

          <Button
            type="submit"
            variant={showGoogle ? 'outline' : 'default'}
            className="w-full"
            disabled={state === 'otp_sending'}
          >
            {state === 'otp_sending' ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Sending code…
              </>
            ) : (
              'Email me a code'
            )}
          </Button>

          {error && (
            <StatePanel variant="error" shape="banner" title={error} className="mt-4" />
          )}

          <div className="mt-4 text-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <a href={signUpHref} className="text-primary hover:underline">
                Sign up
              </a>
            </p>
          </div>
        </form>
      )}

      {(state === 'otp_sent' || state === 'otp_verifying') && (
        <form onSubmit={handleVerifyOTP} className="p-6 max-w-md w-full rounded-panel border border-white/[0.08] bg-surface-raised surface-toplight">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-semibold tracking-[-0.02em] text-foreground">Enter your code</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {codeAlreadyRequested ? 'A code was already requested for ' : 'We sent a temporary login code to '}
              <span className="font-medium text-foreground" data-ph-mask>
                {email}
              </span>
              {codeAlreadyRequested
                ? ' in the last minute. Enter it if you have it, or get a new one when the timer runs out.'
                : '.'}
            </p>
            <button
              type="button"
              onClick={handleEditEmail}
              className="mt-3 text-sm text-primary hover:underline"
            >
              Use a different email
            </button>
          </div>

          <div className="mb-6">
            <OTPInput
              length={6}
              value={otp}
              onChange={setOtp}
              disabled={state === 'otp_verifying'}
              error={!!error}
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={state === 'otp_verifying' || otp.length !== 6}
          >
            {state === 'otp_verifying' ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Verifying…
              </>
            ) : (
              'Verify code'
            )}
          </Button>

          {error && (
            <StatePanel variant="error" shape="banner" title={error} className="mt-4" />
          )}

          <div className="mt-4 text-center">
            <p className="text-muted-foreground text-sm">
              Didn't receive the code?{' '}
              {resendTimer > 0 ? (
                <span className="text-muted-foreground tabular-nums">
                  Resend in {formatResend(resendTimer)}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleResendOTP}
                  className="text-primary hover:underline"
                >
                  Resend code
                </button>
              )}
            </p>
          </div>
        </form>
      )}

      <MobileReviewerSignin />
      <footer
        className="mt-6 w-full max-w-md text-xs text-muted-foreground"
        aria-label="Sign-in footer"
      >
        <ul className="grid grid-cols-2 gap-x-4 gap-y-2 sm:flex sm:flex-wrap sm:items-center sm:justify-center sm:gap-x-3">
          <li className="flex items-center justify-center gap-1.5 sm:contents">
            <span className="inline-flex items-center gap-1.5">
              <Lock className="size-3" aria-hidden="true" />
              Secure sign-in
            </span>
          </li>
          <li className="hidden sm:inline text-muted-foreground/50" aria-hidden="true">·</li>
          <li className="flex items-center justify-center">
            <button
              type="button"
              onClick={handleOpenHelp}
              className="min-h-[32px] inline-flex items-center hover:text-foreground hover:underline underline-offset-2"
            >
              Need help?
            </button>
          </li>
          <li className="hidden sm:inline text-muted-foreground/50" aria-hidden="true">·</li>
          <li className="flex items-center justify-center">
            <a
              href="/Terms_of_service.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="min-h-[32px] inline-flex items-center hover:text-foreground hover:underline underline-offset-2"
            >
              Terms
            </a>
          </li>
          <li className="hidden sm:inline text-muted-foreground/50" aria-hidden="true">·</li>
          <li className="flex items-center justify-center">
            <a
              href="/privacy_policy.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="min-h-[32px] inline-flex items-center hover:text-foreground hover:underline underline-offset-2"
            >
              Privacy
            </a>
          </li>
        </ul>
      </footer>
    </div>
  );
}


